import useSWR from 'swr';
import useCart from 'hooks/shop/useCart';

const multiFetcher = (...urls) => {
  const f = u => fetch(u).then(r => r.json());
  if (urls.length > 1) {
    return Promise.all(urls.map(f));
  }
  return f(urls);
};


const useAddonDiscount = (addonConfigs, locale, shopifyData) => {
  const configs = Array.isArray(addonConfigs) ? addonConfigs.filter(c => c?.discountId) : [];
  const { cart } = useCart();

  // Batch fetch all discounts in one SWR call
  const discountUrls = configs.map(
    c => `/api/shopify/get-automatic-discount-by-id/?discountId=${c.discountId}&locale=${locale}`
  );
  const { data: discountsRaw } = useSWR(
    discountUrls.length ? discountUrls : null,
    multiFetcher
  );
  const discountsArray = discountsRaw
    ? (Array.isArray(discountsRaw) ? discountsRaw : [discountsRaw])
    : [];

  const productId = shopifyData?.productId;
  const productCollectionIds = shopifyData?.collectionIds || [];
  const currentProductGid = productId ? `gid://shopify/Product/${productId}` : null;
  const cartLines = cart?.lines?.edges || [];

  // Process each discount
  const eligible = [];
  let addonProductMatch = null;

  discountsArray.forEach((resp, i) => {
    const discount = resp?.data;
    if (!discount || discount.status !== 'ACTIVE') return;

    const buysItems = discount.customerBuys?.items;
    const buysCollections = buysItems?.type === 'DiscountCollections'
      ? buysItems.collections?.edges?.map(({ node }) => node?.id) || [] : [];
    const buysProducts = buysItems?.type === 'DiscountProducts'
      ? buysItems.products?.edges?.map(({ node }) => node?.id) || [] : [];
    const buysAllItems = buysItems?.type === 'AllDiscountItems';

    const getsItems = discount.customerGets?.items;
    const addonGid = getsItems?.type === 'DiscountProducts'
      ? getsItems.products?.edges?.[0]?.node?.id : null;
    if (!addonGid) return;

    // Check eligibility (current product is in customerBuys)
    let isEligible = false;
    if (discount.type === 'DiscountAutomaticBasic') isEligible = true;
    else if (discount.type === 'DiscountAutomaticBxgy') {
      if (buysAllItems) isEligible = true;
      else if (buysCollections.length > 0) isEligible = buysCollections.some(id => productCollectionIds.includes(id));
      else if (buysProducts.length > 0) isEligible = buysProducts.includes(currentProductGid);
    }

    // Parse discount value
    const getsValue = discount.customerGets?.value;
    let discountPct = null;
    let discountAmt = null;
    if (getsValue?.type === 'DiscountPercentage') discountPct = getsValue.percentage;
    else if (getsValue?.type === 'DiscountAmount') discountAmt = parseFloat(getsValue.amount?.amount);
    else if (getsValue?.type === 'DiscountOnQuantity') {
      if (getsValue.effect?.percentage != null) discountPct = getsValue.effect.percentage;
      else if (getsValue.effect?.amount?.amount != null) discountAmt = parseFloat(getsValue.effect.amount.amount);
    }

    const calcSalePrice = fullPrice => {
      if (!fullPrice) return null;
      let price = null;
      if (discountPct != null) price = (parseFloat(fullPrice) * (1 - discountPct)).toFixed(2);
      else if (discountAmt != null) price = (parseFloat(fullPrice) - discountAmt).toFixed(2);
      return price && parseFloat(price) > 0 ? price : null;
    };

    if (isEligible) {
      eligible.push({
        addonProductId: addonGid.split('/').pop(),
        checkboxLabel: configs[i]?.checkboxLabel,
        calcSalePrice,
      });
    }

    // Check if current product IS this addon (for CartDiscountPrice)
    if (addonGid === currentProductGid) {
      let hasQualifying = false;
      if (buysAllItems && cartLines.length > 0) hasQualifying = true;
      else {
        const cartCollectionIds = cartLines.flatMap(
          ({ node }) => node?.merchandise?.product?.collections?.edges?.map(({ node: col }) => col?.id) || []
        );
        const cartProductIds = cartLines.map(({ node }) => node?.merchandise?.product?.id).filter(Boolean);
        if (buysCollections.length > 0) hasQualifying = buysCollections.some(id => cartCollectionIds.includes(id));
        if (!hasQualifying && buysProducts.length > 0) hasQualifying = buysProducts.some(id => cartProductIds.includes(id));
      }
      if (hasQualifying) {
        addonProductMatch = { salePrice: calcSalePrice(shopifyData?.productPrice) };
      }
    }
  });

  // Batch fetch all addon product data in one SWR call
  const productUrls = eligible.map(
    e => `/api/shopify/utils/getProduct?productId=${e.addonProductId}&locale=${locale}`
  );
  const { data: productsRaw } = useSWR(
    productUrls.length ? productUrls : null,
    multiFetcher,
    { refreshInterval: 60000 }
  );
  const productsArray = productsRaw
    ? (Array.isArray(productsRaw) ? productsRaw : [productsRaw])
    : [];

  // Build final eligible addons with product data
  const eligibleAddons = eligible.map((e, i) => {
    const productData = productsArray[i]?.status === 200 ? productsArray[i].data : null;
    if (!productData || productData.outOfStock) return null;
    return {
      checkboxLabel: e.checkboxLabel || productData.productTitle,
      fullPrice: productData.productPrice,
      salePrice: e.calcSalePrice(productData.productPrice),
      variantId: productData.variantId,
    };
  }).filter(Boolean);

  return {
    eligibleAddons,
    isAddonProduct: !!addonProductMatch,
    hasQualifyingCartItem: !!addonProductMatch,
    salePrice: addonProductMatch?.salePrice || null,
  };
};

export default useAddonDiscount;
